<div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50">
  <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">

    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
      <div class="flex items-center gap-3">
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">
          Línea de tiempo de la inscripción #{{ enrollmentId }}
        </h3>
        @if (timeline) {
          <span class="px-2 py-0.5 rounded-full text-xs font-medium"
                [ngClass]="timeline.enrollment.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                           timeline.enrollment.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' :
                           timeline.enrollment.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                           timeline.enrollment.status === 'CANCELLED' ? 'bg-gray-200 text-gray-600' :
                           'bg-gray-100 text-gray-500'">
            {{ timeline.enrollment.statusLabel || statusText(timeline.enrollment.status) }}
          </span>
        }
      </div>
      <div class="flex items-center gap-2">
        <label class="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
          <input type="checkbox" [(ngModel)]="includeTestRuns" (change)="load()"
                 class="w-3.5 h-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"/>
          Incluir pruebas
        </label>
        <button (click)="load()" [disabled]="loading"
                class="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors" title="Recargar">
          <svg class="w-4 h-4" [class.animate-spin]="loading" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
        </button>
        <button (click)="closed.emit()" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
      </div>
    </div>

    <!-- Body -->
    <div class="overflow-y-auto flex-1 p-6">
      @if (loading && !timeline) {
        <div class="flex items-center justify-center py-12 text-gray-400">
          <svg class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
          Cargando timeline...
        </div>
      } @else if (error) {
        <p class="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{{ error }}</p>
      } @else if (timeline) {

        @if (retrySuccess) {
          <div class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400 flex justify-between">
            <span>{{ retrySuccess }}</span>
            <button (click)="retrySuccess = ''">&times;</button>
          </div>
        }

        <!-- Enrollment summary -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5 text-sm">
          <div class="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
            <dt class="text-xs text-gray-500 mb-0.5">Registro</dt>
            <!-- inc40 — nombre del registro; el id queda al lado para depurar. -->
            <dd class="font-medium text-gray-900 dark:text-white">{{ registroLabel }}</dd>
          </div>
          <div class="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
            <dt class="text-xs text-gray-500 mb-0.5">Inscrito el</dt>
            <dd class="text-gray-900 dark:text-white">{{ timeline.enrollment.enrolledAt | date:'dd/MM/yyyy HH:mm' }}</dd>
          </div>
          @if (timeline.enrollment.completedAt || timeline.enrollment.exitedAt) {
            <div class="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
              <dt class="text-xs text-gray-500 mb-0.5">{{ timeline.enrollment.completedAt ? 'Completado' : 'Salida' }}</dt>
              <dd class="text-gray-900 dark:text-white">
                {{ (timeline.enrollment.completedAt ?? timeline.enrollment.exitedAt) | date:'dd/MM/yyyy HH:mm' }}
              </dd>
            </div>
          }
          @if (timeline.enrollment.exitReason) {
            <div class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 col-span-2 sm:col-span-3">
              <dt class="text-xs text-amber-600 dark:text-amber-400 mb-0.5">Motivo de salida</dt>
              <dd class="text-sm text-amber-800 dark:text-amber-300">{{ timeline.enrollment.exitReasonLabel || exitReasonText(timeline.enrollment.exitReason) }}</dd>
              @if (timeline.enrollment.exitMessage) {
                <dd class="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{{ timeline.enrollment.exitMessage }}</dd>
              }
            </div>
          }
          @if (timeline.enrollment.lastError) {
            <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 col-span-2 sm:col-span-3">
              <dt class="text-xs text-red-600 dark:text-red-400 mb-0.5">Último error</dt>
              <dd class="text-xs font-mono text-red-600 dark:text-red-300 break-all">{{ timeline.enrollment.lastError }}</dd>
            </div>
          }
        </div>

        <!-- Summary metrics -->
        @if (timeline.summary) {
          <div class="flex flex-wrap gap-2 mb-5">
            @if (timeline.summary.totalRuns) {
              <div class="px-3 py-1.5 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-xs text-gray-600 dark:text-gray-300">
                <span class="font-semibold">{{ timeline.summary.totalRuns }}</span> ejecuciones
              </div>
            }
            @if (timeline.summary.totalSteps) {
              <div class="px-3 py-1.5 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-xs text-gray-600 dark:text-gray-300">
                <span class="font-semibold">{{ timeline.summary.totalSteps }}</span> pasos
              </div>
            }
            @if (timeline.summary.successfulSteps) {
              <div class="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg text-xs text-green-700 dark:text-green-400">
                <span class="font-semibold">{{ timeline.summary.successfulSteps }}</span> exitosos
              </div>
            }
            @if (timeline.summary.failedSteps) {
              <div class="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 rounded-lg text-xs text-red-700 dark:text-red-400">
                <span class="font-semibold">{{ timeline.summary.failedSteps }}</span> fallidos
              </div>
            }
            @if (timeline.summary.skippedSteps) {
              <div class="px-3 py-1.5 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-xs text-gray-500 dark:text-gray-400">
                <span class="font-semibold">{{ timeline.summary.skippedSteps }}</span> omitidos
              </div>
            }
            @if (timeline.summary.retryScheduledSteps) {
              <div class="px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg text-xs text-orange-700 dark:text-orange-400">
                <span class="font-semibold">{{ timeline.summary.retryScheduledSteps }}</span> en reintento
              </div>
            }
            @if (timeline.summary.durationMs) {
              <div class="px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg text-xs text-indigo-700 dark:text-indigo-400">
                Duración: <span class="font-semibold">{{ formatDuration(timeline.summary.durationMs) }}</span>
              </div>
            }
          </div>
        }

        <!-- Runs -->
        @if (timeline.runs.length === 0) {
          <p class="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
            No hay ejecuciones registradas.
          </p>
        } @else {
          <div class="space-y-3">
            @for (run of timeline.runs; track run.id) {
              <div class="border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden">
                <!-- Run header -->
                <button type="button" (click)="toggleRun(run.id)"
                        class="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700/40 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors text-left">
                  <svg class="w-4 h-4 text-gray-400 transition-transform shrink-0"
                       [class.rotate-90]="expandedRuns.has(run.id)"
                       fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                  <span class="font-mono text-xs text-gray-400">Ejecución #{{ run.id }}</span>
                  <span class="px-1.5 py-0.5 rounded text-[10px] font-bold" [ngClass]="getRunBadgeClass(run.runType)">
                    {{ run.runTypeLabel || runTypeText(run.runType) }}
                  </span>
                  <span class="px-2 py-0.5 rounded-full text-xs font-medium"
                        [ngClass]="getStepStatusClass(run.status)">{{ run.statusLabel || statusText(run.status) }}</span>
                  @if (run.startedAt) {
                    <span class="text-xs text-gray-400 ml-auto">{{ run.startedAt | date:'dd/MM HH:mm:ss' }}</span>
                  }
                  @if (run.steps.length > 0) {
                    <span class="text-xs text-gray-400 shrink-0">{{ run.steps.length }} paso(s)</span>
                  }
                </button>

                <!-- Run steps (collapsible) -->
                @if (expandedRuns.has(run.id)) {
                  <div class="p-4">
                    @if (run.errorMessage) {
                      <div class="mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                        <p class="text-xs font-mono text-red-600 dark:text-red-300">{{ run.errorMessage }}</p>
                      </div>
                    }
                    @if (run.steps.length === 0) {
                      <p class="text-xs text-gray-400 text-center py-2">Sin pasos registrados.</p>
                    } @else {
                      <div class="space-y-2">
                        @for (step of run.steps; track step.id) {
                          <div class="rounded-lg border overflow-hidden"
                               [ngClass]="step.status.toUpperCase() === 'FAILED' || step.status.toUpperCase() === 'RETRY_SCHEDULED'
                                 ? 'border-red-200 dark:border-red-800'
                                 : step.status.toUpperCase() === 'SUCCESS'
                                 ? 'border-green-200 dark:border-green-800'
                                 : 'border-gray-200 dark:border-gray-600'">
                            <!-- Step header -->
                            <div class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/30 flex-wrap">
                              <span class="text-[10px] text-gray-400" [title]="step.nodeId ? ('nodo #' + step.nodeId) : ''">{{ nodeText(step.nodeId) }}</span>
                              <span class="text-xs font-medium text-gray-700 dark:text-gray-300">{{ step.stepTypeLabel || nodeTypeText(step.stepType) }}</span>
                              @if (step.stepSubtype) {
                                <span class="text-xs text-gray-400">/ {{ step.stepSubtypeLabel || nodeSubtypeText(step.stepSubtype) }}</span>
                              }
                              <span class="px-1.5 py-0.5 rounded-full text-[10px] font-medium ml-auto"
                                    [ngClass]="getStepStatusClass(step.status)">{{ step.statusLabel || statusText(step.status) }}</span>
                              @if (step.attemptNumber) {
                                <span class="text-[10px] text-gray-400">intento {{ step.attemptNumber }}</span>
                              }
                              @if (step.durationMs) {
                                <span class="text-[10px] text-gray-400">{{ formatDuration(step.durationMs) }}</span>
                              }
                              @if (hasJson(step.outputJson) || hasJson(step.errorJson)) {
                                <button (click)="toggleStepOutput(step.id)"
                                        class="text-[10px] text-blue-500 hover:text-blue-700 px-1.5 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                                  {{ expandedStepOutputs.has(step.id) ? 'Ocultar' : 'Ver JSON' }}
                                </button>
                              }
                              @if (isRetryable(step)) {
                                <button (click)="openRetryModal(step.id)"
                                        class="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded hover:bg-orange-200 transition-colors font-semibold">
                                  Reintentar
                                </button>
                              }
                            </div>
                            <!-- Step timestamps -->
                            @if (step.startedAt) {
                              <div class="px-3 py-1.5 flex gap-4 text-[10px] text-gray-400 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                                <span>Inicio: {{ step.startedAt | date:'HH:mm:ss.SSS' }}</span>
                                @if (step.finishedAt) {
                                  <span>Fin: {{ step.finishedAt | date:'HH:mm:ss.SSS' }}</span>
                                }
                                @if (step.nextRetryAt) {
                                  <span class="text-orange-500">Reintento: {{ step.nextRetryAt | date:'dd/MM HH:mm' }}</span>
                                }
                              </div>
                            }
                            <!-- Step JSON output/error (expandible) -->
                            @if (expandedStepOutputs.has(step.id)) {
                              <div class="px-3 pb-3 border-t border-gray-100 dark:border-gray-700 pt-2 bg-white dark:bg-gray-800">
                                @if (hasJson(step.errorJson)) {
                                  <p class="text-[10px] font-semibold text-red-500 mb-1">Error JSON</p>
                                  <pre class="text-[10px] font-mono bg-red-50 dark:bg-red-900/20 p-2 rounded overflow-x-auto max-h-28 text-red-600 dark:text-red-300">{{ step.errorJson | json }}</pre>
                                }
                                @if (hasJson(step.outputJson)) {
                                  <p class="text-[10px] font-semibold text-gray-500 mt-2 mb-1">Output JSON</p>
                                  <pre class="text-[10px] font-mono bg-gray-50 dark:bg-gray-700/40 p-2 rounded overflow-x-auto max-h-28 text-gray-700 dark:text-gray-300">{{ step.outputJson | json }}</pre>
                                }
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      }
    </div>
  </div>
</div>

<!-- Modal: Reintentar paso -->
@if (showRetryModal) {
  <div class="fixed inset-0 z-modal-top flex items-center justify-center bg-black/60">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
      <h4 class="text-base font-semibold text-gray-900 dark:text-white mb-2">Reintentar paso</h4>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
        El paso se volverá a encolar para ejecución. Puedes indicar un motivo opcional.
      </p>
      @if (retryError) {
        <p class="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg mb-3">{{ retryError }}</p>
      }
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Motivo (opcional)</label>
        <input type="text" [(ngModel)]="retryReason" placeholder="Ej: Error transitorio corregido"
               class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"/>
      </div>
      <div class="flex gap-2 justify-end">
        <button (click)="closeRetryModal()" [disabled]="retrying"
                class="px-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
          Cancelar
        </button>
        <button (click)="confirmRetry()" [disabled]="retrying"
                class="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center gap-2">
          @if (retrying) {
            <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
          }
          Reintentar
        </button>
      </div>
    </div>
  </div>
}
